/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { API, CONFIGUREROUTES, DIALOGOPENFOR } from "../../../globals/globals";
import {
  useGetDusListQuery,
  useGetNrCellsQuery,
  useGetRadioNodesQuery,
  useGetZoneCellsQuery,
  useSetDeviceConfigMutation,
} from "../../../store/api/deviceConfig/deviceConfigApiSlice";
import { nrCellsColumns } from "../../../components/Shared/Table/Columns";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { deviceVersionSelector } from "../../../store/filters/Nodes/selectors";
import { confirmDialogSelector, paginationModelSelector } from "../../../store/helpers/helperSelectors";
import { setConfigCounts } from "../../../store/api/deviceConfig/reducers";
import { useStoreDispatch } from "../../../store/hooks";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import React from "react";
import { INrCell, INrCellDataGridRow } from "../../../store/api/deviceConfig/types";
import { OPERATIONTYPES, constStr } from "../../../globals/constants";
import { setOpenToast } from "../../../store/helpers/helperReducers";
import { prepareNrCellPayload } from "../../../components/Configuration/helpers";
import { errorMessage } from "../../../utils/helpers";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import ProfilePermissions from "../../../utils/ProfilePermissions";
import { initialState } from "../../../store/helpers/helperInitialState";

export const useNrCellsData = () => {
  const location = useLocation();
  const dispatch = useStoreDispatch();
  const locationPath = location?.pathname.split("/");
  const swVersion = useSelector(deviceVersionSelector);
  const { nrCell = initialState.pagination } = useSelector(paginationModelSelector);
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

  const [status, setStatus] = useState("");
  const [alarm, setAlarm] = useState("");

  const openConfirmDialog = useSelector(confirmDialogSelector);
  const selectedDevice = useSelector(selectedDeviceSelector);
  const [setDeviceConfig] = useSetDeviceConfigMutation();
  const [selectedNrCellRow, setSelectedNrCellRow] = React.useState<INrCellDataGridRow>();
  const [selectedNrCellDetails, setSelectedNrCellDetails] = React.useState<INrCell | null>(null);
  const [rowClicked, setRowClicked] = useState(false);

  const args = {
    filters: `page=${nrCell.page.toString()}&size=${nrCell.pageSize.toString()}&status=${status.toString()}&alarm=${alarm.toString()}&swVersion=${swVersion}`,
    deviceKey: locationPath[2],
  };
  const { data, isLoading, refetch } = useGetNrCellsQuery(args, {
    pollingInterval: API.ENDPOINTS.NRCELLS.pollingInterval || API.POLLING_INTERVAL,
  });

  const zoneArgs = {
    filters: `page=${"0"}&size=${"100"}`,
    deviceKey: locationPath[2],
    deviceNode: selectedDevice?.nodeType,
  };
  const { data: zoneList } = useGetZoneCellsQuery(zoneArgs, {
    pollingInterval: API.ENDPOINTS.LTEZONECELLS.pollingInterval || API.POLLING_INTERVAL,
  });

  const rnArgs = {
    filters: `page=${"0"}&size=${"100"}`,
    deviceKey: locationPath[2],
  };

  const { data: radioNodeList } = useGetRadioNodesQuery(rnArgs, {
    pollingInterval: API.ENDPOINTS.RADIONODES.pollingInterval || API.POLLING_INTERVAL,
    skip: selectedDevice?.modelName?.toLowerCase() === "SCSN-10000-3"?.toLowerCase(),
  });

  // const { data: radioNodeList } = useGetRadioNodesQuery(rnArgs);

  const { data: radioUnitList } = useGetDusListQuery(rnArgs, {
    pollingInterval: API.ENDPOINTS.RADIONODES.pollingInterval || API.POLLING_INTERVAL,
    skip: selectedDevice?.modelName?.toLowerCase() !== "SCSN-10000-3"?.toLowerCase(),
  });

  const deleteNrCellHandler = async (actionName: string) => {
    if (!actionName) return;
    try {
      let response: any;
      let messageStr: string = "";
      if (actionName.match(constStr.yesDelete.toLowerCase())) {
        if (openConfirmDialog === DIALOGOPENFOR.NRCELL) {
          const nrCellayload =
            !!selectedDevice?.objectKey && prepareNrCellPayload(selectedNrCellRow, selectedDevice?.objectKey, OPERATIONTYPES.DELETE);
          response = await setDeviceConfig(nrCellayload);
          messageStr = `The deletion process for NR Cell has been initiated.`;
        }

        if (response?.error) throw response.error;
        if (response?.data?.status === 200 || response?.data?.statusCodeValue === 200 || response?.data?.statusCode === "OK") {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: messageStr,
            })
          );
          refetch();
        }
      }
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  useEffect(() => {
    if (!!data?.data) {
      dispatch(setConfigCounts({ [CONFIGUREROUTES.NRCELL.Path]: data?.totalItems || 0 }));
    }
  }, [data?.data, data?.totalItems, dispatch]);

  useEffect(() => {
    refetch();
  }, [status, alarm, refetch]);

  return {
    permissions,
    data,
    isLoading,
    nrCellsColumns,
    setStatus,
    setAlarm,
    deleteNrCellHandler,
    setSelectedNrCellRow,
    zoneList,
    selectedNrCellDetails,
    setSelectedNrCellDetails,
    rowClicked,
    setRowClicked,
    radioNodeList,
    radioUnitList,
  };
};
